import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { Board, Panel, Stat, StatGrid } from "@/components/primitives";
import { StageTag, StatusPill } from "@/components/status";
import { useNow, useYard } from "@/lib/use-yard";
import { ON_SITE, fmtClock, fmtDuration, waitMinutes } from "@/lib/yard-data";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Gate Manifest — PortDwellr" },
      { name: "description", content: "Live gate manifest: every truck on the yard, its place in the queue and its wait timer." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: GateManifestPage,
});

function GateManifestPage() {
  return (
    <AppShell>
      <GateManifest />
    </AppShell>
  );
}

function GateManifest() {
  const { trucks, events, settings } = useYard();
  const now = useNow();

  const onSite = trucks.filter((t) => ON_SITE.includes(t.status));
  const waits = onSite.map((t) => waitMinutes(t, now));
  const avgWait = waits.length ? Math.round(waits.reduce((a, b) => a + b, 0) / waits.length) : 0;
  const overThreshold = onSite.filter((t) => waitMinutes(t, now) > settings.freeTimeHours * 60).length;

  const queue = [...onSite].sort((a, b) => a.arrivedAt - b.arrivedAt);

  return (
    <>
      <div className="mb-8 flex flex-col gap-3 border-b border-line pb-7 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="eyebrow">SENSE · live gate</span>
          <h1 className="mt-4 font-display text-[30px] font-semibold leading-[1.1] md:text-[36px]">Gate Manifest</h1>
          <p className="mt-3 max-w-[62ch] text-[15px] text-ink-soft">
            {settings.yardName ? `${settings.yardName} — e` : "E"}very truck that has touched the gate today, in the
            order SEQUENCE is running them.
          </p>
        </div>
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-steel-mid">
          Free time threshold · {settings.freeTimeHours}h
        </span>
      </div>

      <StatGrid>
        <Stat value={onSite.length} label="Trucks on-site right now" sub="gate_in · staged · in_process" />
        <Stat value={fmtDuration(avgWait)} label="Average wait time today" sub="arrival → departure" />
        <Stat
          value={overThreshold}
          tone={overThreshold > 0 ? "signal" : "steel"}
          label={`Over the ${settings.freeTimeHours}h detention-free window`}
          sub="detention exposure"
        />
      </StatGrid>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1.6fr_1fr]">
        <Board title="Gate Manifest — Live" foot="Sequenced automatically by arrival, appointment window & trailer type">
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full border-collapse font-mono text-[12.5px]">
              <thead>
                <tr>
                  {["Q#", "Carrier", "Trailer", "Status", "Wait"].map((h) => (
                    <th
                      key={h}
                      className="border-b border-paper/20 px-[14px] py-[10px] text-left text-[10.5px] font-medium uppercase tracking-[0.06em] text-paper/55"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {queue.map((t) => {
                  const w = waitMinutes(t, now);
                  const over = w > settings.freeTimeHours * 60;
                  return (
                    <tr key={t.id}>
                      <td className="border-b border-paper/10 px-[14px] py-3 font-semibold text-signal">{t.queueNo}</td>
                      <td className="border-b border-paper/10 px-[14px] py-3">{t.carrier}</td>
                      <td className="border-b border-paper/10 px-[14px] py-3 text-paper/75">{t.trailerType}</td>
                      <td className="border-b border-paper/10 px-[14px] py-3">
                        <StatusPill status={t.status} dock={t.dock} onDark />
                      </td>
                      <td className={`border-b border-paper/10 px-[14px] py-3 ${over ? "text-signal" : ""}`}>
                        {fmtDuration(w)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile stacked rows — no horizontal scroll */}
          <ul className="md:hidden">
            {queue.map((t) => {
              const w = waitMinutes(t, now);
              const over = w > settings.freeTimeHours * 60;
              return (
                <li key={t.id} className="border-b border-paper/10 px-[14px] py-3 font-mono text-[12.5px] last:border-b-0">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-signal">#{t.queueNo}</span>
                    <span className={over ? "text-signal" : "text-paper/75"}>{fmtDuration(w)}</span>
                  </div>
                  <div className="mt-1">{t.carrier}</div>
                  <div className="mt-1 flex items-center justify-between text-paper/70">
                    <StatusPill status={t.status} dock={t.dock} onDark />
                    <span>{t.trailerType}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </Board>

        <Panel
          title="Agent Event Feed"
          action={
            <span className="inline-flex items-center gap-[7px] font-mono text-[11px] tracking-[0.08em] text-signal-deep">
              <i className="live-pulse inline-block h-[7px] w-[7px] bg-signal" />
              LIVE
            </span>
          }
        >
          <ul className="max-h-[560px] overflow-y-auto">
            {events.map((e) => (
              <li key={e.id} className="border-b border-line px-[18px] py-4 last:border-b-0">
                <div className="flex items-center justify-between gap-3">
                  <StageTag stage={e.stage} />
                  <span className="font-mono text-[11px] text-steel-mid">{fmtClock(e.at)}</span>
                </div>
                <p className="mt-2 text-[13.5px] leading-snug text-ink">{e.message}</p>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </>
  );
}
